import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { programsApi } from '../api/programsApi';
import { CreateOrUpdateProgramPayload } from '../types/program';

export const useCreateOrUpdateProgram = (programId?: number) => {
  const queryClient = useQueryClient();

  const onSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['program-list'] });
    if (programId) {
      queryClient.invalidateQueries({ queryKey: ['program', programId] });
    }
  }, [queryClient, programId]);

  const { mutateAsync: createProgram, isPending: isCreating } = useMutation({
    mutationFn: (name: string) => programsApi.createProgram(name),
    onSuccess,
  });

  const { mutateAsync: updateProgram, isPending: isUpdating } = useMutation({
    mutationFn: (params: Partial<CreateOrUpdateProgramPayload>) =>
      programsApi.updateProgram(params),
    onSuccess,
  });

  const {
    mutateAsync: updatePublished,
    isPending: isUpdatingPublishedProgram,
  } = useMutation({
    mutationFn: (params: Partial<CreateOrUpdateProgramPayload>) =>
      programsApi.updatePublishedProgram(params),
    onSuccess,
  });

  const createOrUpdateProgram = async (
    params: Partial<CreateOrUpdateProgramPayload>
  ) => {
    if (programId) {
      return updateProgram({ id: programId, ...params });
    } else {
      if (!params.name) return;
      const id = await createProgram(params.name);
      await updateProgram({ id, ...params });
      return id;
    }
  };

  const updatePublishedProgram = async (
    params: Partial<CreateOrUpdateProgramPayload>
  ) => {
    if (programId) {
      return updatePublished({ id: programId, ...params });
    }
  };

  return {
    createOrUpdateProgram,
    updatePublishedProgram,
    isLoading: isUpdating || isCreating || isUpdatingPublishedProgram,
  };
};
